import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { useEntityOptions } from '../hooks/useEntityOptions';
import { EntitySelect } from '../components/shared/EntitySelect';
import ModuleAutomationsPanel from '../components/automation/ModuleAutomationsPanel';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, Headphones, MessageSquare, Clock, User, Mail,
  Pencil, Trash2, ChevronDown, ChevronUp, Send, X, AlertTriangle,
  CheckCircle2, TicketIcon, Filter
} from 'lucide-react';
import { ViewToggle, ScrollableTabs, FilterPill } from '../components/ui/view-toggle';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'waiting', label: 'Waiting', color: 'bg-purple-100 text-purple-700' },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-700' },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-600' },
];
const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];
const CATEGORIES = [
  { value: 'general', label: 'General' }, { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' }, { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug_report', label: 'Bug Report' }, { value: 'account', label: 'Account' }, { value: 'other', label: 'Other' },
];

function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }

function TicketModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ subject: '', description: '', category: 'general', priority: 'medium', customer_name: '', customer_email: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);
  const { contacts } = useEntityOptions();
  useEffect(() => {
    if (editing) setForm({ subject: editing.subject || '', description: editing.description || '', category: editing.category || 'general', priority: editing.priority || 'medium', customer_name: editing.customer_name || '', customer_email: editing.customer_email || '', assigned_to: editing.assigned_to || '' });
    else setForm({ subject: '', description: '', category: 'general', priority: 'medium', customer_name: '', customer_email: '', assigned_to: '' });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) { toast.error('Subject required'); return; }
    setSaving(true);
    try {
      const { data } = editing ? await axios.put(`${API}/api/service/tickets/${editing.id}`, form) : await axios.post(`${API}/api/service/tickets`, form);
      toast.success(editing ? 'Updated' : 'Ticket created');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="flex items-center gap-2"><Headphones className="h-5 w-5 text-blue-500" />{editing ? 'Edit' : 'New'} Ticket</DialogTitle><DialogDescription>Create or update a support ticket</DialogDescription></DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-3 mt-2">
        <div><Label>Subject *</Label><Input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))} data-testid="ticket-subject" /></div>
        <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={3} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Category</Label><Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Priority</Label><Select value={form.priority} onValueChange={v => setForm(p => ({...p, priority: v}))}><SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Customer Name</Label><EntitySelect value={form.customer_name} onChange={v => setForm(p => ({...p, customer_name: v}))} onSelect={opt => setForm(p => ({...p, customer_email: opt.email || p.customer_email }))} options={contacts} placeholder="Select or type customer name" listId="ticket-clients" /></div>
          <div><Label>Customer Email</Label><Input type="email" value={form.customer_email} onChange={e => setForm(p => ({...p, customer_email: e.target.value}))} /></div>
        </div>
        <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-ticket-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create Ticket'}</Button></DialogFooter>
      </form></DialogContent></Dialog>
  );
}

// ─── Ticket Detail Panel ───
function TicketDetail({ ticket, onClose, onUpdate }) {
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (ticket) axios.get(`${API}/api/service/tickets/${ticket.id}/replies`).then(r => setReplies(r.data)).catch(() => {});
  }, [ticket]);

  const addReply = async () => {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/api/service/tickets/${ticket.id}/replies`, { message: replyText });
      setReplies(p => [...p, data]);
      setReplyText('');
      toast.success('Reply added');
    } catch { toast.error('Failed'); }
    finally { setPosting(false); }
  };

  const updateStatus = async (status) => {
    try {
      const { data } = await axios.put(`${API}/api/service/tickets/${ticket.id}`, { status });
      onUpdate(data);
      toast.success(`Status: ${status}`);
    } catch { toast.error('Failed'); }
  };

  if (!ticket) return null;
  const statusInfo = STATUSES.find(s => s.value === ticket.status) || STATUSES[0];
  const priInfo = PRIORITIES.find(p => p.value === ticket.priority) || PRIORITIES[1];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ticket-detail-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1"><p className="text-[10px] text-gray-400 font-mono">{ticket.ticket_number}</p><h2 className="text-base font-bold text-gray-900 truncate">{ticket.subject}</h2></div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 flex-shrink-0"><X className="h-4 w-4" /></Button>
        </div>
        <div className="px-5 py-3 flex items-center gap-2 flex-wrap border-b">
          <Badge className={`${statusInfo.color} text-xs`}>{statusInfo.label}</Badge>
          <Badge className={`${priInfo.color} text-xs`}>{priInfo.label}</Badge>
          <Badge variant="outline" className="text-xs">{CATEGORIES.find(c => c.value === ticket.category)?.label || ticket.category}</Badge>
        </div>
        <div className="px-5 py-3 border-b">
          <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
            {ticket.customer_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{ticket.customer_name}</span>}
            {ticket.customer_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{ticket.customer_email}</span>}
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo(ticket.created_at)}</span>
          </div>
          {ticket.description && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{ticket.description}</p>}
        </div>
        <div className="px-5 py-2 border-b">
          <ScrollableTabs>
            {STATUSES.map(s => (
              <Button key={s.value} variant={ticket.status === s.value ? 'default' : 'outline'} size="sm" className={`h-6 text-[10px] flex-shrink-0 ${ticket.status === s.value ? 'bg-blue-600' : ''}`} onClick={() => updateStatus(s.value)} data-testid={`set-status-${s.value}`}>{s.label}</Button>
            ))}
          </ScrollableTabs>
        </div>
        <div className="px-5 py-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Conversation ({replies.length})</p>
          <div className="space-y-3 mb-4">
            {replies.map(r => (
              <div key={r.id} className="bg-gray-50 rounded-lg p-3" data-testid={`reply-${r.id}`}>
                <div className="flex items-center gap-2 mb-1"><span className="text-xs font-semibold text-gray-700">{r.author_name}</span><span className="text-[10px] text-gray-400">{timeAgo(r.created_at)}</span></div>
                <p className="text-sm text-gray-600">{r.message}</p>
              </div>
            ))}
            {replies.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No replies yet</p>}
          </div>
          <div className="flex gap-2">
            <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type a reply..." rows={2} className="flex-1 text-sm" data-testid="reply-input" onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) addReply(); }} />
            <Button onClick={addReply} disabled={posting || !replyText.trim()} className="bg-blue-600 hover:bg-blue-700 self-end" data-testid="send-reply-btn"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HelpDeskPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [view, setView] = useState(() => window.innerWidth < 640 ? 'card' : 'list');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority && filterPriority !== 'all') params.priority = filterPriority;
      const { data } = await axios.get(`${API}/api/service/tickets`, { params,  });
      setTickets(data);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterPriority]);
  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleSaved = (t, isUpdate) => { if (isUpdate) setTickets(p => p.map(x => x.id === t.id ? t : x)); else setTickets(p => [t, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete ticket?')) return; try { await axios.delete(`${API}/api/service/tickets/${id}`); setTickets(p => p.filter(t => t.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };
  const handleUpdate = (updated) => { setTickets(p => p.map(t => t.id === updated.id ? updated : t)); setSelectedTicket(updated); };

  const openCount = tickets.filter(t => ['open', 'in_progress', 'waiting'].includes(t.status)).length;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="helpdesk-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Headphones className="h-5 w-5 text-blue-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Help Desk<PageInfoTooltip pageKey="helpdesk" /></h1><p className="text-xs text-gray-400">{tickets.length} tickets · {openCount} open</p></div></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-32 h-8 text-sm" data-testid="ticket-search" /></div>
          <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-24 h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}><SelectTrigger className="w-24 h-8 text-xs"><SelectValue placeholder="Priority" /></SelectTrigger><SelectContent><SelectItem value="all">All Priority</SelectItem>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
          <ViewToggle view={view} onChange={setView} />
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-8 gap-1 text-xs" data-testid="new-ticket-btn"><Plus className="h-3.5 w-3.5" /> New Ticket</Button>
        </div>
      </div>

      {loading ? <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" /></div> : tickets.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><Headphones className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No tickets</h3><Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 gap-1.5 mt-3"><Plus className="h-4 w-4" /> Create Ticket</Button></CardContent></Card>
      ) : view === 'card' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tickets.map(t => {
            const statusInfo = STATUSES.find(s => s.value === t.status) || STATUSES[0];
            const priInfo = PRIORITIES.find(p => p.value === t.priority) || PRIORITIES[1];
            return (
              <Card key={t.id} className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedTicket(t)} data-testid={`ticket-card-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-1.5 h-8 rounded-full flex-shrink-0 ${t.priority === 'urgent' ? 'bg-red-500' : t.priority === 'high' ? 'bg-orange-400' : t.priority === 'medium' ? 'bg-blue-400' : 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] font-mono text-gray-400">{t.ticket_number}</span>
                      <h3 className="text-sm font-bold text-gray-900 truncate">{t.subject}</h3>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    <Badge className={`${statusInfo.color} text-[9px]`}>{statusInfo.label}</Badge>
                    <Badge className={`${priInfo.color} text-[9px]`}>{priInfo.label}</Badge>
                  </div>
                  {t.description && <p className="text-xs text-gray-500 line-clamp-2 mb-2">{t.description}</p>}
                  <div className="flex items-center justify-between text-[10px] text-gray-400">
                    <span>{t.customer_name || 'No customer'}</span>
                    <div className="flex items-center gap-2">
                      {t.reply_count > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="h-2.5 w-2.5" />{t.reply_count}</span>}
                      <span>{timeAgo(t.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-2 pt-2 border-t" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => { setEditing(t); setModalOpen(true); }}><Pencil className="h-3 w-3" /> Edit</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => handleDelete(t.id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => {
            const statusInfo = STATUSES.find(s => s.value === t.status) || STATUSES[0];
            const priInfo = PRIORITIES.find(p => p.value === t.priority) || PRIORITIES[1];
            return (
              <Card key={t.id} className="border-gray-200 hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setSelectedTicket(t)} data-testid={`ticket-row-${t.id}`}>
                <CardContent className="p-3 sm:p-4 flex items-center gap-3">
                  <div className={`w-1.5 h-10 rounded-full flex-shrink-0 ${t.priority === 'urgent' ? 'bg-red-500' : t.priority === 'high' ? 'bg-orange-400' : t.priority === 'medium' ? 'bg-blue-400' : 'bg-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono text-gray-400">{t.ticket_number}</span>
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{t.subject}</h3>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <Badge className={`${statusInfo.color} text-[9px]`}>{statusInfo.label}</Badge>
                      <Badge className={`${priInfo.color} text-[9px]`}>{priInfo.label}</Badge>
                      {t.customer_name && <span className="text-[10px] text-gray-400">{t.customer_name}</span>}
                      <span className="text-[10px] text-gray-400">{timeAgo(t.created_at)}</span>
                      {t.reply_count > 0 && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><MessageSquare className="h-2.5 w-2.5" />{t.reply_count}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(t); setModalOpen(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(t.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <TicketModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
      {selectedTicket && <TicketDetail ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onUpdate={handleUpdate} />}
      <ModuleAutomationsPanel module="service" title="Service Automations" />
    </div>
  );
}
